import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DiscoveryDistributionSupplierListResponseType } from "@sellar-io/api-v2/types";
import {
  apiV2AssertResponse,
  apiV2Request,
} from "../../../../../hooks/api/v2/apiV2Request";
import { ServerStateKeys } from "../../../../../hooks/api/v2/ServerStateKeys";

interface UseOrderLinkFlowDistributionSuppliersParams {
  buyerPostcode: string | null;
  excludeSupplierId: number | null;
}

export const useOrderLinkFlowDistributionSuppliers = ({
  buyerPostcode,
  excludeSupplierId,
}: UseOrderLinkFlowDistributionSuppliersParams) => {
  const enabled = Boolean(buyerPostcode);

  const suppliersQuery = useQuery({
    queryKey: [
      ServerStateKeys.DiscoveryDistributionSuppliers,
      "orderLinkFlow",
      buyerPostcode,
    ],
    queryFn: () =>
      apiV2Request({
        user: undefined,
        business: null,
        method: "get",
        path: "discovery/distribution-suppliers",
        params: {
          query: [
            {
              type: "retailerPostalCode",
              modifier: "EQUALS",
              value: buyerPostcode,
            },
          ],
          limit: 12,
        },
      }).then(
        (data) =>
          apiV2AssertResponse<DiscoveryDistributionSupplierListResponseType>(
            data,
          ).data,
      ),
    enabled,
    staleTime: 1000 * 60 * 30, // 30 minutes
    retry: (failureCount: number, error: unknown) => {
      const axiosError = error as { response?: { status?: number } };
      return ![400, 401, 404].includes(axiosError.response?.status ?? -1);
    },
  });

  const suppliers = useMemo(() => {
    const allSuppliers = suppliersQuery.data ?? [];
    if (excludeSupplierId === null) return allSuppliers;
    return allSuppliers.filter((supplier) => supplier.id !== excludeSupplierId);
  }, [suppliersQuery.data, excludeSupplierId]);

  return {
    suppliers,
    isLoading: suppliersQuery.isPending,
    isError: suppliersQuery.isError,
    error: suppliersQuery.error,
  };
};
